import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import MarketingHeader from '@/components/marketing/MarketingHeader';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import { Check, Sparkles, ArrowRight } from 'lucide-react';
import { joinWaitlist } from '@/lib/api';
import { trackEvent } from '@/lib/metaPixel';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: 29,
    blurb: 'For owner-operators and small portfolios.',
    cta: 'Start free trial',
    features: [
      'Up to 3 properties',
      'Unlimited bookings',
      'Cleaning + maintenance tasks',
      'Calendar + iCal sync (1 channel)',
      'Expense tracking + receipts',
      'Email support',
    ],
    highlighted: false,
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 79,
    blurb: 'For growing managers juggling 4–15 doors.',
    cta: 'Start free trial',
    features: [
      'Up to 15 properties',
      'Everything in Starter',
      'Unlimited iCal channels (Airbnb, VRBO, Booking, Evolve)',
      'Recurring maintenance',
      'Renovation project tracker',
      '3 staff seats included',
      'Invoice generation',
      'Revenue & profit analytics',
      'Priority email + chat support',
    ],
    highlighted: true,
    badge: 'Most popular',
  },
  {
    id: 'business',
    name: 'Business',
    price: 199,
    blurb: 'For full-service teams with portfolios at scale.',
    cta: 'Start free trial',
    features: [
      'Unlimited properties',
      'Everything in Pro',
      'Unlimited staff seats',
      'Multi-org owner reports',
      'Custom invoice branding',
      'SSO (Google Workspace)',
      'CSV exports & API access',
      'Dedicated success manager',
    ],
    highlighted: false,
  },
];

const FAQ = [
  {
    q: 'Do I need a credit card to start?',
    a: 'Yes — we ask for a card up front to keep the 14-day trial free of bots. You will not be charged until day 15, and you can cancel any time in your account settings.',
  },
  {
    q: 'What happens at the end of the trial?',
    a: "We email you 3 days before the trial ends. If you don't cancel, your card is charged for whichever plan you picked at signup. You can downgrade or upgrade at any time.",
  },
  {
    q: 'Is my data really isolated?',
    a: 'Every record (property, booking, expense, task) is stamped with your organization ID. Queries strictly filter by that ID, end-to-end. Staff you invite inherit only your org.',
  },
  {
    q: 'Can I import from Airbnb / VRBO?',
    a: 'Yes — paste any iCal feed URL and we pull the bookings in (and push your blocked dates back). Setup takes about 30 seconds per channel.',
  },
  {
    q: 'Is there an annual discount?',
    a: 'Yes — annual billing is 2 months free (about 17% off). The toggle will appear at checkout when billing launches.',
  },
  {
    q: 'Can I bring my own contractors and cleaners?',
    a: 'Absolutely. Add them as Worker records, assign them to tasks, and track their hours for invoicing. They never see your books or other workers\' data.',
  },
];

export default function PricingPage() {
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('pro');
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [billingEnabled, setBillingEnabled] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [interval, setInterval] = useState('monthly'); // 'monthly' | 'annual'
  const { user } = useAuth();
  const navigate = useNavigate();

  // Annual = 2 months free (~17% off): displayed as monthly-equivalent price
  // for easy comparison. Actual charge = displayedPrice * 12.
  const priceForPlan = (monthlyPrice) =>
    interval === 'annual' ? Math.round(monthlyPrice * 10) / 12 : monthlyPrice;

  useEffect(() => {
    trackEvent('ViewContent', { content_name: 'Pricing', content_category: 'pricing_page' });
    api.get('/billing/config').then(({ data }) => {
      setBillingEnabled(!!data?.enabled);
    }).catch(() => {});
  }, []);

  const handlePlanCTA = async (planId) => {
    if (!billingEnabled) {
      if (!user) {
        navigate('/register');
      } else {
        toast.info("Free during beta — you're already signed in. Paid plans launch soon.");
        navigate('/dashboard');
      }
      return;
    }
    if (!user) {
      sessionStorage.setItem('intent_plan', planId);
      navigate('/register?plan=' + planId);
      return;
    }
    try {
      setCheckoutLoading(planId);
      const { data } = await api.post('/billing/checkout', {
        plan: planId,
        origin_url: window.location.origin,
        interval,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not start checkout');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not start checkout');
    } finally {
      setCheckoutLoading(null);
    }
  };

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!waitlistEmail.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    try {
      await joinWaitlist({ email: waitlistEmail, plan: selectedPlan, source: 'pricing_page' });
      trackEvent('Lead', { content_name: 'Waitlist - Pricing', content_category: selectedPlan });
      setWaitlistSubmitted(true);
      toast.success("You're on the list. We'll email when billing opens.");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not join waitlist');
    }
  };

  return (
    <div className="min-h-screen bg-[#070d1c] text-slate-100">
      <MarketingHeader />

      {/* HERO */}
      <section className="relative pt-32 pb-12">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/3 h-[420px] w-[420px] rounded-full bg-amber-400/10 blur-3xl" />
          <div className="absolute top-20 right-10 h-[300px] w-[300px] rounded-full bg-sky-500/10 blur-3xl" />
        </div>

        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-amber-300/30 bg-amber-300/10 text-xs text-amber-200 mb-6">
            <Sparkles className="h-3 w-3" />
            Free during beta · Paid plans launch soon
          </div>
          <h1 className="font-manrope font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white leading-[1.05]">
            Pricing that scales with your{' '}
            <span className="bg-gradient-to-r from-amber-200 via-amber-100 to-sky-200 bg-clip-text text-transparent">
              portfolio.
            </span>
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl mx-auto">
            <strong className="text-white">Everything is free while we&apos;re in beta.</strong>{' '}
            The plans below are what pricing will look like once billing goes
            live. Join the waitlist and lock in your tier — you&apos;ll be the
            first we email when billing opens.
          </p>

          {/* Monthly ↔ Annual toggle */}
          <div
            className="mt-8 inline-flex items-center gap-1 rounded-full border border-[#2a4a7a] bg-[#0c1530]/70 p-1 backdrop-blur"
            role="tablist"
            aria-label="Billing interval"
            data-testid="billing-interval-toggle"
          >
            <button
              type="button"
              role="tab"
              aria-selected={interval === 'monthly'}
              onClick={() => setInterval('monthly')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                interval === 'monthly'
                  ? 'bg-amber-400 text-[#070d1c] shadow shadow-amber-500/20'
                  : 'text-slate-300 hover:text-white'
              }`}
              data-testid="billing-interval-monthly"
            >
              Monthly
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={interval === 'annual'}
              onClick={() => setInterval('annual')}
              className={`px-5 py-2 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
                interval === 'annual'
                  ? 'bg-amber-400 text-[#070d1c] shadow shadow-amber-500/20'
                  : 'text-slate-300 hover:text-white'
              }`}
              data-testid="billing-interval-annual"
            >
              Annual
              <span
                className={`text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded ${
                  interval === 'annual'
                    ? 'bg-[#070d1c]/15 text-[#070d1c]'
                    : 'bg-amber-300/15 text-amber-200'
                }`}
              >
                Save 17%
              </span>
            </button>
          </div>
        </div>
      </section>

      {/* PLANS */}
      <section className="pb-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid md:grid-cols-3 gap-5 items-stretch">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id)}
              className={`relative rounded-2xl p-6 lg:p-8 border-2 transition-all cursor-pointer flex flex-col ${
                plan.highlighted
                  ? 'bg-gradient-to-br from-amber-400/10 via-[#0c1530] to-sky-500/5 border-amber-300/50 shadow-2xl shadow-amber-400/10 lg:scale-[1.02]'
                  : selectedPlan === plan.id
                    ? 'bg-[#0c1530] border-[#2a4a7a]'
                    : 'bg-[#0c1530]/60 border-[#1f3a64] hover:border-[#2a4a7a]'
              }`}
              data-testid={`pricing-card-${plan.id}`}
            >
              {plan.badge && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-amber-400 text-[#070d1c] text-xs font-semibold shadow-lg shadow-amber-500/30">
                  {plan.badge}
                </div>
              )}
              <h3 className="font-manrope text-2xl font-bold text-white">{plan.name}</h3>
              <p className="text-sm text-slate-400 mt-2">{plan.blurb}</p>

              <div className="mt-6 flex items-baseline gap-1">
                <span className="font-manrope text-5xl font-bold text-white">
                  ${(() => {
                    const p = priceForPlan(plan.price);
                    // Show whole dollars when annual (already averaged to 2-decimal but we display rounded)
                    return interval === 'annual' ? p.toFixed(0) : p;
                  })()}
                </span>
                <span className="text-slate-400 text-sm">/month</span>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                {interval === 'annual'
                  ? `Billed annually · $${plan.price * 10}/yr (2 months free)`
                  : 'Per organization · billed monthly'}
              </p>

              <ul className="mt-6 space-y-3 flex-1">
                {plan.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-300">
                    <Check className={`h-4 w-4 flex-shrink-0 mt-0.5 ${plan.highlighted ? 'text-amber-300' : 'text-sky-300'}`} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => handlePlanCTA(plan.id)}
                disabled={checkoutLoading === plan.id}
                className={`w-full h-11 font-semibold mt-8 ${
                  plan.highlighted
                    ? 'bg-amber-400 hover:bg-amber-300 text-[#070d1c] shadow-lg shadow-amber-500/20'
                    : 'bg-slate-100 hover:bg-white text-[#070d1c]'
                }`}
                data-testid={`pricing-cta-${plan.id}`}
              >
                {checkoutLoading === plan.id
                  ? 'Starting checkout...'
                  : billingEnabled
                    ? `Start ${plan.name} trial`
                    : plan.cta}
                {checkoutLoading !== plan.id && <ArrowRight className="ml-2 h-4 w-4" />}
              </Button>
              <p className="text-center text-xs text-slate-500 mt-3">
                {billingEnabled
                  ? '14-day free trial · Cancel anytime'
                  : 'Free during beta. Billing starts at general availability.'}
              </p>
            </div>
          ))}
        </div>

        {/* Comparison strip */}
        <div className="mt-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="rounded-xl border border-[#1f3a64] bg-[#0c1530]/60 px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-3 text-sm">
            <span className="text-slate-300">
              <strong className="text-white">Need more than 50 properties?</strong> We have a Growth plan with custom pricing.
            </span>
            <a
              href="mailto:sales@propveiw.com"
              className="text-amber-300 hover:text-amber-200 font-medium inline-flex items-center gap-1"
            >
              Contact sales <ArrowRight className="h-3 w-3" />
            </a>
          </div>
        </div>
      </section>

      {/* WAITLIST */}
      <section className="py-16 bg-gradient-to-br from-[#0c1530] to-[#070d1c] border-y border-[#1f3a64] relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-300/40 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-300/40 to-transparent" />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-manrope text-2xl sm:text-3xl font-bold text-white tracking-tight">
            Save your founder-tier discount.
          </h2>
          <p className="mt-3 text-slate-300">
            Drop your email below and pick the plan you&apos;re eyeing. When billing opens, we&apos;ll
            apply 30% off for the first year — no questions asked.
          </p>

          {!waitlistSubmitted ? (
            <form
              onSubmit={handleWaitlist}
              className="mt-6 flex flex-col sm:flex-row gap-3 max-w-xl mx-auto"
              data-testid="pricing-waitlist-form"
            >
              <Input
                type="email"
                value={waitlistEmail}
                onChange={(e) => setWaitlistEmail(e.target.value)}
                placeholder="you@yourcompany.com"
                className="bg-[#0c1530]/70 border-[#2a4a7a] text-white placeholder:text-slate-500 h-12 flex-1"
                data-testid="pricing-waitlist-email"
                required
              />
              <Button
                type="submit"
                className="bg-amber-400 hover:bg-amber-300 text-[#070d1c] font-semibold h-12 px-6 shadow-lg shadow-amber-500/20"
                data-testid="pricing-waitlist-submit"
              >
                Reserve {PLANS.find((p) => p.id === selectedPlan)?.name} pricing
              </Button>
            </form>
          ) : (
            <div className="mt-6 max-w-md mx-auto rounded-lg border border-amber-300/30 bg-amber-300/10 p-4 text-amber-100 text-sm" data-testid="pricing-waitlist-success">
              <Check className="inline h-4 w-4 mr-1" />
              You&apos;re on the list. We&apos;ll honor your founder discount.
            </div>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-[#070d1c]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <h2 className="font-manrope text-3xl sm:text-4xl font-bold text-white tracking-tight">Common questions</h2>
          <p className="mt-3 text-slate-400">
            Still curious? Email{' '}
            <a href="mailto:hello@propveiw.com" className="text-amber-300 hover:text-amber-200">
              hello@propveiw.com
            </a>
            .
          </p>

          <div className="mt-10 divide-y divide-[#1f3a64] border-y border-[#1f3a64]">
            {FAQ.map((item) => (
              <details key={item.q} className="group py-5">
                <summary className="flex items-start justify-between cursor-pointer text-white font-medium list-none">
                  <span>{item.q}</span>
                  <ArrowRight className="h-4 w-4 text-slate-400 mt-1 transition-transform group-open:rotate-90" />
                </summary>
                <p className="text-slate-400 leading-relaxed mt-3 pr-6">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
